import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";

import { Language } from "imports/Utils";

import { costTypeSchema, CostTypeSchemaInput, CostTypeSchemaOutput } from "./utils/costType.schema";

import { SaveCostTypeDocument } from "__generated__/view/costtypes/graphql/mutations.generated";

const CostTypeCreateEdit = () => {

	const navigate = useNavigate();
	const location = useLocation();

	const { control, handleSubmit } = useForm<CostTypeSchemaInput, unknown, CostTypeSchemaOutput>({
		resolver: zodResolver(costTypeSchema),
		defaultValues: {
			id: location.state?.costType?.id ?? null,
			title_en: location.state?.costType?.title_en ?? "",
			title_nl: location.state?.costType?.title_nl ?? "",
			title_de: location.state?.costType?.title_de ?? "",
		},
	});

	const [ saveCostType, { loading } ] = useMutation(SaveCostTypeDocument);

	const save = (data: CostTypeSchemaOutput) => {
		saveCostType({
			variables: {
				input: {
					id: data.id,
					title_en: data.title_en,
					title_nl: data.title_nl,
					title_de: data.title_de,
				},
			},
		}).then(() => {
			navigate("/view/costtypes");
		});
	};

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("COST_TYPE")}
					<Helmet title={intl.formatMessage({ id: "COST_TYPE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/costtypes")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12 }}>
					<Card>
						<FormGroup
							label={Language("LANGUAGE_EN")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="title_en"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("LANGUAGE_NL")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="title_nl"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("LANGUAGE_DE")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="title_de"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default CostTypeCreateEdit;
